/**
 * 盤トークンAPIサービス
 */

import { apiClient } from '@/shared/lib/axios';
import { authService } from '@/features/auth/services/authService';
import type {
  PanelToken,
  RegisterTokenRequest,
  PanelTokenApiResponse,
  GetAllTokensResponse,
  GetTokenResponse,
} from '../types/panel-token.types';

const PANEL_TOKENS_API_PATH = '/api/panel-tokens';

/**
 * 全盤トークン一覧を取得
 */
const getAll = async (): Promise<readonly PanelToken[]> => {
  const response = await apiClient.get<GetAllTokensResponse>(
    PANEL_TOKENS_API_PATH
  );

  if (!response.data.success) {
    throw new Error(response.data.error || '盤トークン一覧の取得に失敗しました');
  }

  return response.data.tokens || [];
};

/**
 * 特定盤のトークンを取得
 */
const getByPanelId = async (panelId: number): Promise<PanelToken | null> => {
  try {
    const response = await apiClient.get<GetTokenResponse>(
      `${PANEL_TOKENS_API_PATH}/${panelId}`
    );

    if (!response.data.success) {
      return null;
    }

    return response.data.token || null;
  } catch (error) {
    console.error(`Failed to fetch token for panel ${panelId}:`, error);
    return null;
  }
};

/**
 * 盤トークンを登録
 */
const register = async (request: RegisterTokenRequest): Promise<PanelTokenApiResponse> => {
  const csrfToken = await authService.getCsrfToken();

  const response = await apiClient.post<PanelTokenApiResponse>(
    PANEL_TOKENS_API_PATH,
    request,
    {
      headers: {
        'x-csrf-token': csrfToken,
      },
    }
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'トークンの登録に失敗しました');
  }

  return response.data;
};

/**
 * 盤トークンを更新
 */
const update = async (panelId: number, token: string): Promise<PanelTokenApiResponse> => {
  const csrfToken = await authService.getCsrfToken();

  const response = await apiClient.put<PanelTokenApiResponse>(
    `${PANEL_TOKENS_API_PATH}/${panelId}`,
    { token },
    {
      headers: {
        'x-csrf-token': csrfToken,
      },
    }
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'トークンの更新に失敗しました');
  }

  return response.data;
};

/**
 * 盤トークンを無効化
 */
const disable = async (panelId: number): Promise<PanelTokenApiResponse> => {
  const csrfToken = await authService.getCsrfToken();

  const response = await apiClient.patch<PanelTokenApiResponse>(
    `${PANEL_TOKENS_API_PATH}/${panelId}/disable`,
    {},
    {
      headers: {
        'x-csrf-token': csrfToken,
      },
    }
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'トークンの無効化に失敗しました');
  }

  return response.data;
};

/**
 * 盤トークンを有効化
 */
const enable = async (panelId: number): Promise<PanelTokenApiResponse> => {
  const csrfToken = await authService.getCsrfToken();

  const response = await apiClient.patch<PanelTokenApiResponse>(
    `${PANEL_TOKENS_API_PATH}/${panelId}/enable`,
    {},
    {
      headers: {
        'x-csrf-token': csrfToken,
      },
    }
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'トークンの有効化に失敗しました');
  }

  return response.data;
};

/**
 * 盤トークンを削除
 */
const deleteToken = async (panelId: number): Promise<PanelTokenApiResponse> => {
  const csrfToken = await authService.getCsrfToken();

  const response = await apiClient.delete<PanelTokenApiResponse>(
    `${PANEL_TOKENS_API_PATH}/${panelId}`,
    {
      headers: {
        'x-csrf-token': csrfToken,
      },
    }
  );

  if (!response.data.success) {
    throw new Error(response.data.error || 'トークンの削除に失敗しました');
  }

  return response.data;
};

export const panelTokenApiService = {
  getAll,
  getByPanelId,
  register,
  update,
  disable,
  enable,
  delete: deleteToken,
};
